import * as Yup from 'yup';
import {
    stringWithNoSpecialChars,
    validateCivility,
    validateContactType,
    validateDate,
    validateEmail,
    validateNumericId,
    validatePhone
} from "@/Shared/Helpers";
import { ContactEnums } from '@/Shared';

/**
 * Type: ContactFormValues
 *
 * Description:
 * Full definition of the contact form values,
 * including personal information (person) and contact tracking details.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export type ContactFormValues = {
    person: {
        lastname: string,
        firstname: string,
        civility: string,
        profession: string,
        birthDate: string,
        address: string,
        city: string,
        country: string,
        phone1: string,
        phone2: string,
        email: string,
    },
    contactType: string,
    origin: string,
    agentId: string,
    isClosed: boolean,
    note: string,
};

/**
 * =====================================================
 * ContactValidationSchema
 * =====================================================
 *
 * Description:
 * Yup schema for validating the contact form.
 * Uses helper functions to validate text fields, email, phone numbers, dates, civility, and contact type.
 *
 * @param viewEnums - Enumerations used to validate civility and contactType
 * @returns Complete Yup schema for ContactFormValues
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export const ContactValidationSchema = (viewEnums?: ContactEnums) => {
    if(!viewEnums) {
        throw new Error("viewEnums is required to validate this form.")
    }
    
    return Yup.object({

    // ---------------------------
    // Block: Personal information
    // ---------------------------
    person: Yup.object({

        lastname: stringWithNoSpecialChars({ minLength: 2, maxLength: 50 }).required('Le nom est requis'),

        firstname: stringWithNoSpecialChars({ minLength: 2, maxLength: 50 }).nullable().notRequired(),

        profession: stringWithNoSpecialChars({ maxLength: 50 }).nullable(),

        address: stringWithNoSpecialChars({ maxLength: 100 }).nullable(),

        city: stringWithNoSpecialChars({ maxLength: 50 }).nullable(),

        country: stringWithNoSpecialChars({ maxLength: 50 }).nullable(),

        civility: validateCivility(viewEnums.CivilityEnum),

        email: validateEmail().notRequired(),

        birthDate: validateDate().nullable(),

        phone1: validatePhone().nullable(),

        phone2: validatePhone().nullable(),
    }),

    // ---------------------------
    // Block: Contact and tracking
    // ---------------------------
    note: stringWithNoSpecialChars({ maxLength: 500 }).nullable(),

    origin: stringWithNoSpecialChars({ maxLength: 50 }).nullable(),

    contactType: validateContactType(viewEnums.ContactTypeEnum),

    agent: validateNumericId().notRequired(),
});
}


export const RentalCommitmentFormValidationSchema = Yup.object({
    id: Yup.number().required('Le preneur est obligatoire'),
    propertyId: Yup.number().required('Le logement est obligatoire'),
    agentId: Yup.number().required('Le conseiller est obligatoire'),
});